import { Mail, Phone, Instagram, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

export function About() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <span className="label-eyebrow text-muted-foreground">About</span>
      <h1 className="mt-2 font-display text-4xl tracking-tighter">EarnMate</h1>
      <p className="mt-4 text-foreground/80 leading-relaxed">EarnMate is a mobile-first marketplace built for two distinct economies: temporary event staffing and skill-based freelance services. We exist because finding short-notice event crew or hiring a designer for a one-off project shouldn't take days of phone calls.</p>
      <p className="mt-4 text-foreground/80 leading-relaxed">Whether you're a catering captain managing a 200-person wedding, a college student earning weekend cash as event crew, or a graphic designer building a portfolio of paid client work — EarnMate is built around your workflow.</p>
      <h2 className="mt-10 font-display text-2xl">What's included</h2>
      <ul className="mt-3 space-y-2 list-disc list-inside text-foreground/80 text-sm">
        <li>Two-step Post-a-Job wizard with separate flows for events vs. freelance</li>
        <li>Auto waitlist + slot management for managers</li>
        <li>Public, SEO-indexable profile pages for workers, managers and freelancers</li>
        <li>Location-radius filtering (5/10/25/50 km)</li>
        <li>In-app notifications for confirmations, waitlist updates, hire requests</li>
      </ul>
      <Link to="/register" className="btn-primary mt-8 inline-flex">Get started</Link>
    </div>
  );
}

export function FAQ() {
  const items = [
    { q: "Do I have to choose between Worker, Manager and Freelancer?", a: "Yes — for now, each account has one primary role. You can always create a separate account for a different role." },
    { q: "Is EarnMate free?", a: "Posting and applying are free during early access." },
    { q: "How does the waitlist work?", a: "When confirmed slots are full, new applicants automatically join the waitlist. If a confirmed worker drops out or the manager increases slots, the next person in the waitlist is promoted automatically." },
    { q: "Can I share a job listing on WhatsApp?", a: "Yes — every job has its own public URL with WhatsApp/Telegram/Email/Copy share buttons." },
    { q: "Can clients see freelancer portfolios without signing up?", a: "Yes. Profiles are public and SEO-indexable. Hiring requires sign-in." },
  ];
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <h1 className="font-display text-4xl tracking-tighter">FAQ</h1>
      <div className="mt-8 space-y-3">
        {items.map((it, i) => (
          <details key={i} className="card-flat group">
            <summary className="cursor-pointer font-medium list-none flex items-start justify-between gap-4">
              <span>{it.q}</span>
              <span className="text-brand text-xl leading-none group-open:rotate-45 transition">+</span>
            </summary>
            <p className="mt-3 text-sm text-muted-foreground">{it.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

export function Contact() {
  const PHONE = "+919061673057";
  const PHONE_DISPLAY = "+91 90616 73057";
  const EMAIL = "earnmate.in@gmail.com";
  const INSTAGRAM = "earnmate.in";

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12" data-testid="contact-page">
      <span className="label-eyebrow text-muted-foreground">Get in touch</span>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl tracking-tighter">Contact us</h1>
      <p className="mt-4 text-foreground/80 max-w-xl">Have a question, feedback or partnership idea? Reach out — we read every message.</p>

      <div className="mt-10 grid gap-3 sm:grid-cols-2">
        <a href={`mailto:${EMAIL}`} className="card-flat group flex items-start gap-3" data-testid="contact-email">
          <div className="w-10 h-10 rounded-full bg-brand-tint text-brand grid place-items-center shrink-0"><Mail className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="label-eyebrow text-muted-foreground">Email</p>
            <p className="mt-1 font-medium break-all group-hover:text-brand">{EMAIL}</p>
          </div>
        </a>

        <a href={`tel:${PHONE}`} className="card-flat group flex items-start gap-3" data-testid="contact-phone">
          <div className="w-10 h-10 rounded-full bg-brand-tint text-brand grid place-items-center shrink-0"><Phone className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="label-eyebrow text-muted-foreground">Phone</p>
            <p className="mt-1 font-medium group-hover:text-brand">{PHONE_DISPLAY}</p>
          </div>
        </a>

        <a href={`https://wa.me/${PHONE.replace("+","")}`} target="_blank" rel="noreferrer" className="card-flat group flex items-start gap-3" data-testid="contact-whatsapp">
          <div className="w-10 h-10 rounded-full bg-brand-tint text-brand grid place-items-center shrink-0"><MessageSquare className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="label-eyebrow text-muted-foreground">WhatsApp</p>
            <p className="mt-1 font-medium group-hover:text-brand">Chat with us</p>
          </div>
        </a>

        <a href={`https://instagram.com/${INSTAGRAM}`} target="_blank" rel="noreferrer" className="card-flat group flex items-start gap-3" data-testid="contact-instagram">
          <div className="w-10 h-10 rounded-full bg-brand-tint text-brand grid place-items-center shrink-0"><Instagram className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="label-eyebrow text-muted-foreground">Instagram</p>
            <p className="mt-1 font-medium group-hover:text-brand">@{INSTAGRAM}</p>
          </div>
        </a>
      </div>

      <div className="mt-10 card-flat">
        <p className="label-eyebrow text-muted-foreground">Office hours</p>
        <p className="mt-2 text-foreground/80">Monday – Saturday · 10:00 AM – 7:00 PM IST</p>
        <p className="mt-1 text-sm text-muted-foreground">We respond to emails within 24 hours.</p>
      </div>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <span className="label-eyebrow text-muted-foreground">Legal</span>
      <h1 className="mt-2 font-display text-4xl tracking-tighter">Privacy Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: January 2025</p>

      <div className="mt-8 space-y-8 text-foreground/80 leading-relaxed">
        <section>
          <h2 className="font-display text-xl text-foreground mb-3">1. Information We Collect</h2>
          <p>When you register and use EarnMate, we collect the following information:</p>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>Name, email address, and phone number</li>
            <li>Profile photo and username</li>
            <li>Location data (city/area) for job matching</li>
            <li>Job application history and attendance records</li>
            <li>Ratings and reviews</li>
            <li>Device information and IP address</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">2. How We Use Your Information</h2>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>To create and manage your account</li>
            <li>To match workers with job opportunities</li>
            <li>To send notifications about applications and job updates</li>
            <li>To calculate and display ratings and points</li>
            <li>To prevent fraud and maintain platform safety</li>
            <li>To improve our services and user experience</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">3. Information Sharing</h2>
          <p className="text-sm">We do not sell your personal information to third parties. We share your information only in the following circumstances:</p>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>With event managers when you apply for a job (name, username, rating)</li>
            <li>With workers when a manager posts a job (manager name, contact details)</li>
            <li>With service providers who help us operate the platform (MongoDB Atlas, Render, Vercel)</li>
            <li>When required by law or to protect our legal rights</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">4. Data Storage & Security</h2>
          <p className="text-sm">Your data is stored securely on MongoDB Atlas servers. We use industry-standard encryption (HTTPS/TLS) for all data transmission. Passwords are hashed using bcrypt and never stored in plain text.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">5. Your Rights</h2>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>Access your personal data at any time through your profile</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and associated data</li>
            <li>Withdraw consent for data processing</li>
          </ul>
          <p className="mt-3 text-sm">To exercise these rights, contact us at <a href="mailto:earnmate.in@gmail.com" className="text-brand hover:underline">earnmate.in@gmail.com</a></p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">6. Cookies</h2>
          <p className="text-sm">We use cookies to keep you logged in and improve your experience. See our <a href="/cookies" className="text-brand hover:underline">Cookie Policy</a> for details.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">7. Contact Us</h2>
          <p className="text-sm">For privacy-related questions, contact us at:<br />
            📧 <a href="mailto:earnmate.in@gmail.com" className="text-brand hover:underline">earnmate.in@gmail.com</a><br />
            📞 +91 9061673057
          </p>
        </section>
      </div>
    </div>
  );
}

export function TermsOfService() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <span className="label-eyebrow text-muted-foreground">Legal</span>
      <h1 className="mt-2 font-display text-4xl tracking-tighter">Terms of Service</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: January 2025</p>

      <div className="mt-8 space-y-8 text-foreground/80 leading-relaxed">
        <section>
          <h2 className="font-display text-xl text-foreground mb-3">1. Acceptance of Terms</h2>
          <p className="text-sm">By creating an account on EarnMate, you agree to these Terms of Service. If you do not agree, please do not use our platform.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">2. User Responsibilities</h2>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>Provide accurate and truthful information in your profile</li>
            <li>Maintain the confidentiality of your account credentials</li>
            <li>Not impersonate other users or create fake accounts</li>
            <li>Not use the platform for illegal activities</li>
            <li>Treat other users with respect</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">3. Attendance & No-Show Policy</h2>
          <p className="text-sm">Workers who confirm a job are expected to attend. The following consequences apply for no-shows:</p>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>1st no-show: -5 points, warning notification</li>
            <li>2nd no-show: -5 points, final warning</li>
            <li>3rd no-show: Account temporarily suspended</li>
          </ul>
          <p className="mt-2 text-sm">To restore a suspended account, contact earnmate.in@gmail.com</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">4. Job Postings</h2>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>Managers must post accurate job details including payment, date and location</li>
            <li>EarnMate is not responsible for payment disputes between managers and workers</li>
            <li>Fraudulent job postings will result in immediate account termination</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">5. Payments</h2>
          <p className="text-sm">EarnMate facilitates connections between managers and workers. Payment arrangements are made directly between the parties. EarnMate does not process or guarantee payments at this time.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">6. Account Termination</h2>
          <p className="text-sm">We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, or harm other users.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">7. Limitation of Liability</h2>
          <p className="text-sm">EarnMate is a platform connecting managers and workers. We are not liable for any disputes, damages, or losses arising from interactions between users on our platform.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">8. Governing Law</h2>
          <p className="text-sm">These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts in Kerala, India.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">9. Contact</h2>
          <p className="text-sm">📧 <a href="mailto:earnmate.in@gmail.com" className="text-brand hover:underline">earnmate.in@gmail.com</a><br />
            📞 +91 9061673057
          </p>
        </section>
      </div>
    </div>
  );
}

export function CookiePolicy() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <span className="label-eyebrow text-muted-foreground">Legal</span>
      <h1 className="mt-2 font-display text-4xl tracking-tighter">Cookie Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: January 2025</p>

      <div className="mt-8 space-y-8 text-foreground/80 leading-relaxed">
        <section>
          <h2 className="font-display text-xl text-foreground mb-3">What Are Cookies?</h2>
          <p className="text-sm">Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences and keep you logged in.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">Cookies We Use</h2>
          <div className="mt-3 space-y-4">
            <div className="card-flat">
              <p className="font-medium text-foreground text-sm">Essential Cookies</p>
              <p className="text-sm text-muted-foreground mt-1">Required for the platform to function. These keep you logged in and secure your session. Cannot be disabled.</p>
              <p className="text-xs text-muted-foreground mt-1">Cookie: <code className="bg-muted px-1 rounded">access_token</code> — Duration: 7 days</p>
            </div>
            <div className="card-flat">
              <p className="font-medium text-foreground text-sm">Preference Cookies</p>
              <p className="text-sm text-muted-foreground mt-1">Remember your theme preference (dark/light mode) and other settings.</p>
              <p className="text-xs text-muted-foreground mt-1">Stored in: localStorage</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">Third-Party Cookies</h2>
          <p className="text-sm">We use Google OAuth for sign-in. Google may set its own cookies. Please refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-brand hover:underline">Google's Privacy Policy</a> for details.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">Managing Cookies</h2>
          <p className="text-sm">You can control cookies through your browser settings. Note that disabling essential cookies will prevent you from staying logged in to EarnMate.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-foreground mb-3">Contact</h2>
          <p className="text-sm">📧 <a href="mailto:earnmate.in@gmail.com" className="text-brand hover:underline">earnmate.in@gmail.com</a></p>
        </section>
      </div>
    </div>
  );
}
